import { forwardRef, useImperativeHandle, useState } from 'react'

import type { OnlyFansList } from '@api'
import type { SelectProps } from '@radix-ui/react-select'
import { Trigger as PrimitiveTrigger } from '@radix-ui/react-select'

import { IconLoading } from '@/assets/icons/icon-loading'
import { Select, SelectContent, SelectItem } from '@/components/ui/select'

import { useVerticalInfiniteScroll } from '../../hooks/use-vertical-infinite-scroll'
import type { TinyChatsList } from '../../types'
import { unescapeHtmlText } from '../chat-window/fan-info/utils'

interface Props {
  selectedItems?: OnlyFansList['id'][]
  items: TinyChatsList[]
  onSelect: (id: OnlyFansList['id']) => void
  trigger: ({ isOpen }: { isOpen: boolean }) => JSX.Element
  canLoadMore: boolean
  onLoadMore: () => void
  isLoadingMore: boolean
  disabled?: boolean
  disabledItems?: OnlyFansList['id'][]
  onOpen?: () => void
  selectProps?: SelectProps
}

export interface ListsDropdownRef {
  open: () => void
}

export const ListsDropdown = forwardRef<ListsDropdownRef, Props>(
  (
    {
      items,
      onSelect,
      trigger,
      canLoadMore,
      onLoadMore,
      isLoadingMore,
      disabled,
      onOpen,
      disabledItems = [],
      selectProps,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false)

    const handleOpenChange = (open: boolean) => {
      if (open && onOpen) {
        onOpen()
      }

      setIsOpen(open)
    }

    useImperativeHandle(
      ref,
      () => ({
        open: () => handleOpenChange(true),
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    )

    const { parentRef, virtualizer, handleScroll } = useVerticalInfiniteScroll({
      itemHeight: 32,
      itemsLength: items.length,
      onLoadMore,
      canLoadMore: canLoadMore && isOpen,
    })

    return (
      <Select
        onValueChange={onSelect}
        open={isOpen}
        onOpenChange={handleOpenChange}
        {...selectProps}
      >
        <PrimitiveTrigger
          className="outline-none"
          disabled={disabled}
          asChild
        >
          {trigger({ isOpen })}
        </PrimitiveTrigger>
        <SelectContent
          position="popper"
          align="end"
          className="relative min-w-[268px]"
        >
          <div
            className="relative max-h-[256px] overflow-auto"
            ref={parentRef}
            onScroll={handleScroll}
          >
            <div
              className="relative w-full"
              style={{
                height: `${virtualizer.getTotalSize()}px`,
              }}
            >
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const list = items[virtualItem.index]
                const isDisabled = disabledItems.includes(list.id)

                return (
                  <SelectItem
                    data-index={virtualItem.index}
                    value={String(list.id)}
                    key={list.id}
                    className="absolute w-full"
                    ref={virtualizer.measureElement}
                    disabled={isDisabled}
                    style={{
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    {unescapeHtmlText(list.name)}
                    {isDisabled && (list as OnlyFansList).usersCount === 0 && (
                      <span className="ml-1 text-xs text-gray-400">
                        (No chats)
                      </span>
                    )}
                  </SelectItem>
                )
              })}
            </div>
          </div>
          {isLoadingMore && (
            <div className="absolute bottom-0 left-0 right-0 mr-1.5 bg-card">
              <IconLoading className="mx-auto my-3 animate-spin" />
            </div>
          )}
        </SelectContent>
      </Select>
    )
  },
)

ListsDropdown.displayName = 'ListsDropdown'
